import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from 'context/AuthContext';
import { safeJSONParse } from 'utils/helpers/safeParsing';

interface CustomersListContextValue {
    showSpinner: boolean;
    setShowSpinner: (show: boolean) => void;
    customersOrder: string;
    setCustomersOrder: (order: string) => void;
    customersDesc: boolean;
    setCustomersDesc: (desc: boolean) => void;
    filterClientId: number | null;
    setFilterClientId: (clientId: number | null) => void;
    filterVatEnabled: 0 | 1 | null;
    setFilterVatEnabled: (vatEnabled: 0 | 1 | null) => void;
    listResetNonce: number;
    triggerListReset: () => void;
    listRefreshNonce: number;
    triggerListRefresh: () => void;
}

const CustomersListContext = createContext<CustomersListContextValue | null>(null);

export const useCustomersList = () => {
    const ctx = useContext(CustomersListContext);
    if (!ctx) {
        throw new Error('useCustomersList must be used within CustomersListProvider');
    }
    return ctx;
};

export const CustomersListProvider = ({ children }: { children: ReactNode }) => {
    const { auth } = useAuth();
    const filterName = `voltox-${auth.user.ID}-customers-filter-client`;
    const vatFilterName = `voltox-${auth.user.ID}-customers-filter-vat`;
    const orderName = `voltox-${auth.user.ID}-customers-order`;
    const descName = `voltox-${auth.user.ID}-customers-desc`;

    const [showSpinner, setShowSpinner] = useState(false);
    const [listResetNonce, setListResetNonce] = useState(0);
    const [listRefreshNonce, setListRefreshNonce] = useState(0);
    const [customersOrder, setCustomersOrder] = useState(safeJSONParse(sessionStorage.getItem(orderName)) || '');
    const [customersDesc, setCustomersDesc] = useState(safeJSONParse(sessionStorage.getItem(descName)) === true);
    const [filterClientId, setFilterClientId] = useState<number | null>(() => {
        const parsed = safeJSONParse(sessionStorage.getItem(filterName));
        const id = Number(parsed);
        return id > 0 ? id : null;
    });
    const [filterVatEnabled, setFilterVatEnabled] = useState<0 | 1 | null>(() => {
        const parsed = safeJSONParse(sessionStorage.getItem(vatFilterName));
        if (parsed === 0 || parsed === 1) return parsed;
        return null;
    });

    const triggerListReset = () => setListResetNonce((nonce) => nonce + 1);
    const triggerListRefresh = () => setListRefreshNonce((nonce) => nonce + 1);

    useEffect(() => {
        sessionStorage.setItem(filterName, JSON.stringify(filterClientId || null));
    }, [filterClientId, filterName]);

    useEffect(() => {
        sessionStorage.setItem(vatFilterName, JSON.stringify(filterVatEnabled));
    }, [filterVatEnabled, vatFilterName]);

    useEffect(() => {
        sessionStorage.setItem(orderName, JSON.stringify(customersOrder));
    }, [customersOrder, orderName]);

    useEffect(() => {
        sessionStorage.setItem(descName, JSON.stringify(customersDesc));
    }, [customersDesc, descName]);

    return (
        <CustomersListContext.Provider
            value={{
                showSpinner,
                setShowSpinner,
                customersOrder,
                setCustomersOrder,
                customersDesc,
                setCustomersDesc,
                filterClientId,
                setFilterClientId,
                filterVatEnabled,
                setFilterVatEnabled,
                listResetNonce,
                triggerListReset,
                listRefreshNonce,
                triggerListRefresh,
            }}
        >
            {children}
        </CustomersListContext.Provider>
    );
};
